import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import type { ManagedRole, PermissionDefinition } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PROTECTED_WORKFLOW_PERMISSIONS, normalizeRole } from '@/lib/permissions';
import { cn } from '@/lib/utils';

const schema = z.object({
  name: z.string().trim().min(2, 'Role name is required').max(64),
  description: z.string().trim().max(300).optional(),
  permissions: z.array(z.string()).min(1, 'Select at least one permission'),
  isActive: z.boolean(),
});
type FormData = z.infer<typeof schema>;

export default function RoleFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { user, hasPermission, hasAnyRole } = useAuth();
  const isSystemAdmin = hasAnyRole(['ORG_ADMIN', 'Admin']);
  const actorPermissions = user?.permissions ?? [];

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '', permissions: [], isActive: true },
  });
  const selected = watch('permissions') ?? [];
  const isActive = watch('isActive');

  const { data: catalogue = [], isLoading: loadingCatalogue } = useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: PermissionDefinition[] }>('/roles/permissions');
      return response.data ?? [];
    },
  });
  const { data: role, isLoading: loadingRole } = useQuery({
    queryKey: ['managed-role', id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: ManagedRole }>(`/roles/${id}`);
      return response.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (role) reset({ name: role.name, description: role.description ?? '', permissions: role.permissionIds ?? [], isActive: role.isActive });
  }, [role, reset]);

  const groups = useMemo(() => {
    const grouped = new Map<string, PermissionDefinition[]>();
    catalogue.forEach((permission) => grouped.set(permission.group, [...(grouped.get(permission.group) ?? []), permission]));
    return [...grouped.entries()];
  }, [catalogue]);

  // Canonical roles keep their name and workflow approval permissions fixed,
  // but the tenant System Admin can edit description, status, and the
  // non-mandatory permissions. The ORG_ADMIN role is fully protected and can
  // never be edited, renamed, deactivated or deleted.
  const isCanonical = !!role?.isSystem;
  const isProtectedRole = normalizeRole(role?.name ?? '') === 'ORG_ADMIN';
  const canManageCustom = isEdit ? hasPermission('roles:update') : hasPermission('roles:create');
  const readOnly = isProtectedRole || (isCanonical ? !isSystemAdmin : !canManageCustom);
  const isProtected = (key: string) => (PROTECTED_WORKFLOW_PERMISSIONS as readonly string[]).includes(key);

  const canGrant = (key: string) => !isProtected(key) && (isSystemAdmin || actorPermissions.includes(key));
  const togglePermission = (key: string) => {
    if (readOnly || !canGrant(key)) return;
    setValue('permissions', selected.includes(key) ? selected.filter((item) => item !== key) : [...selected, key], { shouldValidate: true, shouldDirty: true });
  };
  const toggleGroup = (items: PermissionDefinition[]) => {
    if (readOnly) return;
    const grantable = items.filter((item) => canGrant(item.key)).map((item) => item.key);
    const allSelected = grantable.length > 0 && grantable.every((key) => selected.includes(key));
    setValue('permissions', allSelected
      ? selected.filter((key) => !grantable.includes(key))
      : [...new Set([...selected, ...grantable])], { shouldValidate: true, shouldDirty: true });
  };

  const mutation = useMutation({
    mutationFn: async (values: FormData) => {
      if (!isEdit) return api.post('/roles', values);
      if (!role) return;
      if (role.isSystem) {
        await api.put(`/roles/${role.id}`, { description: values.description || null, isActive: values.isActive });
      } else {
        await api.put(`/roles/${role.id}`, { name: values.name, description: values.description || null, isActive: values.isActive });
      }
      await api.put(`/roles/${role.id}/permissions`, { permissions: values.permissions });
    },
    onSuccess: () => {
      addToast(`Role ${isEdit ? 'updated' : 'created'} successfully.`, 'success');
      queryClient.invalidateQueries({ queryKey: ['managed-roles'] });
      queryClient.invalidateQueries({ queryKey: ['assignable-roles'] });
      navigate('/admin/roles');
    },
    onError: (error: any) => addToast(error.message || 'Unable to save role.', 'error'),
  });

  if (loadingCatalogue || (isEdit && loadingRole)) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Role' : 'Create Role'}</h1>
        <p className="text-sm text-secondary mt-1">
          {isProtectedRole
            ? 'The ORG_ADMIN role is protected. It cannot be edited, renamed, deactivated or deleted.'
            : isCanonical
              ? 'Edit the description, status, and non-mandatory permissions for this canonical role. Its name and approval workflow permissions are fixed.'
              : 'Choose exactly what users assigned to this role can access.'}
        </p>
      </div>

      <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Role details</CardTitle></CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="role-name">Role Name *</Label>
              <Input id="role-name" {...register('name')} disabled={readOnly || isCanonical} className={cn(errors.name && 'border-error')} />
              <FormError message={errors.name?.message} />
              {isProtectedRole
                ? <p className="text-xs text-secondary mt-1">The ORG_ADMIN role is protected and cannot be renamed.</p>
                : isCanonical && <p className="text-xs text-secondary mt-1">Canonical role names are used by the application and cannot be renamed.</p>}
            </div>
            <div>
              <Label htmlFor="role-status">Status *</Label>
              <Select value={isActive ? 'active' : 'inactive'} onValueChange={(value) => setValue('isActive', value === 'active', { shouldDirty: true })} disabled={readOnly}>
                <SelectTrigger id="role-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-secondary mt-1">{isProtectedRole ? 'The ORG_ADMIN role is protected and its status cannot be changed.' : 'Inactive roles cannot be assigned to new users.'}</p>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="role-description">Description</Label>
              <Textarea id="role-description" {...register('description')} disabled={readOnly} rows={3} className={cn(errors.description && 'border-error')} />
              <FormError message={errors.description?.message} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Access Permissions *</CardTitle>
                <p className="text-xs text-secondary mt-1">{selected.length} permission(s) selected{isCanonical ? ' - workflow approval permissions are fixed.' : ''}</p>
              </div>
              {!readOnly && <Button type="button" variant="outline" size="sm" onClick={() => setValue('permissions', selected.filter((key) => isProtected(key)), { shouldValidate: true })}>Clear all</Button>}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {groups.map(([group, items]) => {
              const grantable = items.filter((item) => canGrant(item.key));
              const allSelected = grantable.length > 0 && grantable.every((item) => selected.includes(item.key));
              return (
                <section key={group} className="rounded-xl border p-4" style={{ borderColor: 'var(--border-subtle)' }}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 className="font-semibold text-primary">{group}</h2>
                    {!readOnly && grantable.length > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => toggleGroup(items)}>{allSelected ? 'Clear group' : 'Select group'}</Button>}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {items.map((permission) => {
                      const disabled = readOnly || !canGrant(permission.key);
                      return (
                        <label key={permission.key} className={cn('flex items-start gap-3 rounded-lg border p-3', disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-surface-secondary')}>
                          <input type="checkbox" className="mt-1 h-4 w-4 accent-blue-600" checked={selected.includes(permission.key)} disabled={disabled} onChange={() => togglePermission(permission.key)} />
                          <span><span className="block text-sm font-medium text-primary">{permission.label}</span><span className="block text-xs text-secondary mt-0.5">{permission.description || permission.key}</span></span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              );
            })}
            <FormError message={errors.permissions?.message} />
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/roles')}>Back</Button>
          {!readOnly && <Button type="submit" loading={mutation.isPending}>{isEdit ? 'Save Changes' : 'Create Role'}</Button>}
        </div>
      </form>
    </div>
  );
}
